import { Layout, LinkAction, Loading, Page, SkeletonBodyText } from '@shopify/polaris'
import { useCallback, useState } from 'react'
import { useParams } from 'react-router-dom'
import useSWR, { mutate } from 'swr'

import { Result } from '../common/api'
import { ApiActionDialog } from '../common/ApiActionDialog'
import { Footer } from '../common/Footer'
import { ResultToastOrBanner, showIntercom, useDialogActivator, useQuery } from '../common/helpers'
import { LoadErrorPage } from '../common/LoadErrorPage'
import { useSaveBarContext } from '../common/SaveBar'
import { useShopifyReview } from '../common/ShopifyReviewHandler'
import { TabIndex, Tabs } from '../common/Tabs'
import * as urls from '../common/urls'
import { SellingPlansContext, useSellingPlansSWR } from '../selling-plans-index-page/api'
import {
  convertWorkflowToFormData,
  createOrUpdateWorkflow,
  fetchWorkflowFormData,
  WorkflowFormData,
  WorkflowInitialParams,
} from './api'
import { WorkflowForm } from './WorkflowForm'

const useWorkflowFormData = (id?: string, initialParams: WorkflowInitialParams = {}) =>
  useSWR<WorkflowFormData>(
    ['workflow', id],
    (_: any, id?: string) => fetchWorkflowFormData(_, id, initialParams),
    {
      refreshInterval: 0,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      refreshWhenHidden: false,
      refreshWhenOffline: false,
    }
  )

const Skeleton = () => (
  <Layout>
    <Layout.Section>
      <SkeletonBodyText lines={10} />
      <br />
      <SkeletonBodyText lines={10} />
    </Layout.Section>
    <Layout.Section variant="oneThird">
      <SkeletonBodyText lines={7} />
    </Layout.Section>
  </Layout>
)

export const WorkflowPage = () => {
  const { id } = useParams<{ id?: string }>()
  const { hideAndRedirect } = useSaveBarContext()
  const query = useQuery()

  const secondaryPriceAdjustmentSellingPlanId = query.get('secondary_price_adjustment') ?? undefined
  const spgId = query.get('spg_id') ?? undefined
  const shopifyReviewRequest = useShopifyReview()

  const sellingPlansSWR = useSellingPlansSWR()
  const workflowFormSWR = useWorkflowFormData(id, {
    secondaryPriceAdjustmentSellingPlanId,
  })
  const workflowFormData = workflowFormSWR.data

  const [isSaving, setSaving] = useState(false)

  const deleteDialogActivator = useDialogActivator()

  const [saveResult, setSaveResult] = useState<Result<any> | null>(null)

  const onSave = useCallback(
    async (data: WorkflowFormData) => {
      setSaving(true)

      const res = await createOrUpdateWorkflow(data, id)

      setSaving(false)

      setSaveResult(res)

      if (res.status !== 'success' || !res.data) {
        return
      }

      if (id) {
        mutate(['workflow', id], convertWorkflowToFormData(res.data), false)
      } else {
        const params = {
          spg_id: spgId,
          review: shopifyReviewRequest.canSkipNpsSurvey ? 'workflow-created' : undefined,
        }
        hideAndRedirect(urls.workflowUrl(res.data!.id, params), 'replace')
      }
    },
    [id, hideAndRedirect, spgId, shopifyReviewRequest.canSkipNpsSurvey]
  )

  const afterDelete = useCallback(() => {
    hideAndRedirect(urls.workflowsUrl, 'replace')
  }, [hideAndRedirect])

  const backAction: LinkAction = spgId
    ? { content: 'Plans', url: urls.planUrl(spgId) }
    : { content: 'Workflows', url: urls.workflowsUrl }

  const primaryAction =
    id && workflowFormData
      ? {
          content: 'Delete',
          onAction: deleteDialogActivator.show,
          disabled: isSaving,
          destructive: true,
        }
      : undefined
  const secondaryActions =
    id && workflowFormData
      ? [
          {
            content: 'Chat with us',
            onAction: () => showIntercom('I have a question about creating a workflow.'),
          },
          {
            content: workflowFormData.is_active ? 'Disable' : 'Activate',
            onAction: () => onSave({ ...workflowFormData, is_active: !workflowFormData.is_active }),
            disabled: isSaving,
          },
        ]
      : [
          {
            content: 'Chat with us',
            onAction: () => showIntercom('I have questions about editing workflows.'),
          },
        ]

  return (
    <>
      {(workflowFormSWR.isValidating || isSaving) && <Loading />}
      <Tabs selected={TabIndex.Workflows} />

      {workflowFormSWR.error && <LoadErrorPage />}
      {!workflowFormSWR.error && (
        <Page
          title={id ? 'Edit workflow' : 'Create workflow'}
          backAction={backAction}
          primaryAction={primaryAction}
          secondaryActions={secondaryActions}
        >
          {(!workflowFormData || !sellingPlansSWR.data) && <Skeleton />}

          {workflowFormData && sellingPlansSWR.data && (
            <SellingPlansContext.Provider value={sellingPlansSWR.data ?? []}>
              <ResultToastOrBanner result={saveResult} setResult={setSaveResult} />
              <WorkflowForm
                initialData={workflowFormData}
                onSave={onSave}
                isSaving={isSaving}
                saveResult={saveResult}
                isNew={!id}
              />
            </SellingPlansContext.Provider>
          )}
          <Layout>
            <Layout.Section>
              <Footer />
            </Layout.Section>
          </Layout>
        </Page>
      )}

      {id && (
        <ApiActionDialog
          activator={deleteDialogActivator}
          onSuccess={afterDelete}
          url={`/workflows/${id}`}
          method="DELETE"
          message="This action cannot be reverted"
          destructive={true}
          primaryButtonTitle="Delete"
        />
      )}
    </>
  )
}
